import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";

// Four themes (2026-06-09): dark (:root default), light (.light-mode),
// calm (.calm-mode — Sage Twilight) and modern (.modern-mode — Linear-
// style neutral dark + electric blue accent + sharper corners). toggleTheme
// cycles dark → light → calm → modern → dark; setTheme jumps directly.
type Theme = "dark" | "light" | "calm" | "modern";

const THEMES: Theme[] = ["dark", "light", "calm", "modern"];

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: "light",
  toggleTheme: () => {},
  setTheme: () => {},
});

// localStorage can be null OR throw on access (storage disabled, some privacy
// modes, sandboxed iframes). A raw access here ran at the app root and crashed
// the entire page — white screen for the visitor (Sentry: "Cannot read
// properties of null (reading 'getItem')", Chrome/Windows, production,
// 2026-07-07). Every access is now guarded; the theme just falls back to the
// default when storage is unavailable.
const THEME_KEY = "trybeup_theme_v2";
function readStoredTheme(): Theme | null {
  try {
    const stored = window.localStorage?.getItem(THEME_KEY);
    return stored === "light" || stored === "dark" || stored === "calm" || stored === "modern"
      ? (stored as Theme)
      : null;
  } catch {
    return null;
  }
}
function writeStoredTheme(theme: Theme): void {
  try {
    window.localStorage?.setItem(THEME_KEY, theme);
  } catch {
    /* storage unavailable — theme still applies for this session */
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    // v2 storage key so the light-shell prototype shows for every visitor
    // on first load. The previous "trybeup_theme" value is ignored.
    return readStoredTheme() ?? "light";
  });

  useEffect(() => {
    const root = document.documentElement;
    // Mutually exclusive theme classes. Dark is the bare :root default
    // (no class), so we only ever add one of the override classes.
    root.classList.remove("light-mode", "calm-mode", "modern-mode");
    if (theme === "light") root.classList.add("light-mode");
    else if (theme === "calm") root.classList.add("calm-mode");
    else if (theme === "modern") root.classList.add("modern-mode");
    writeStoredTheme(theme);

    // Keep the browser/PWA chrome tint (<meta name="theme-color">) in
    // sync with the active theme. Each theme uses its own status-zone tone:
    //   dark   → near-black charcoal (#11100e)
    //   light  → cream (#faf8f5)
    //   calm   → sage (#323e33) — matches body::before band + header
    //   modern → warm near-black (#100f0d) — silver-gelatin shadow
    const themeColor = theme === "light"
      ? "#faf8f5"
      : theme === "calm"
        ? "#323e33"
        : theme === "modern"
          ? "#100f0d"
          : "#11100e";
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "theme-color");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", themeColor);

    // iOS status-bar TEXT color must follow the band tone, or the
    // clock + battery icons disappear. The safe-area band is now
    // per-theme (charcoal / cream / sage / black), so the icons need:
    //   light (cream band)                 → Style.Light = DARK icons
    //   dark + calm + modern (dark bands)  → Style.Dark  = LIGHT icons
    if (Capacitor.isNativePlatform()) {
      const style = theme === "light" ? Style.Light : Style.Dark;
      StatusBar.setStyle({ style }).catch(() => { /* no-op off-native */ });
    }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setThemeState(prev => {
      const idx = THEMES.indexOf(prev);
      return THEMES[(idx + 1) % THEMES.length];
    });
  }, []);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
